import AsyncStorage from '@react-native-async-storage/async-storage';

export interface CachedEntry<T> {
  data: T;
  timestamp: number;
  ttl: number;
}

export interface CacheStats {
  totalEntries: number;
  sizeKB: number;
}

export interface IndexedAsyncStorageCacheOptions {
  indexKey: string;
  logTag: string;
  defaultTtlMs: number;
  quotaEvictionBatch?: number;
  maxQuotaRetries?: number;
  cleanupOnInit?: boolean;
}

export function isQuotaExceededError(error: unknown): boolean {
  if (typeof DOMException !== 'undefined' && error instanceof DOMException && error.name === 'QuotaExceededError') {
    return true;
  }
  if (error instanceof Error) {
    const msg = error.message || '';
    if (msg.includes('QuotaExceededError') || msg.includes('exceeded the quota') || msg.includes('QuotaExceeded')) {
      return true;
    }
  }
  return false;
}

export class IndexedAsyncStorageCache<TData> {
  protected cacheIndex: Set<string> = new Set();
  protected initialized = false;
  protected initializationPromise: Promise<void> | null = null;
  protected quotaExhausted = false;
  protected readonly indexKey: string;
  protected readonly logTag: string;
  protected readonly defaultTtlMs: number;
  protected readonly quotaEvictionBatch: number;
  protected readonly maxQuotaRetries: number;
  protected readonly cleanupOnInit: boolean;

  constructor(options: IndexedAsyncStorageCacheOptions) {
    this.indexKey = options.indexKey;
    this.logTag = options.logTag;
    this.defaultTtlMs = options.defaultTtlMs;
    this.quotaEvictionBatch = options.quotaEvictionBatch ?? 50;
    this.maxQuotaRetries = options.maxQuotaRetries ?? 3;
    this.cleanupOnInit = options.cleanupOnInit ?? true;
  }

  async initialize(): Promise<void> {
    if (this.initialized) return;
    if (this.initializationPromise) return this.initializationPromise;

    this.initializationPromise = (async () => {
      const startTime = Date.now();
      try {
        const indexData = await AsyncStorage.getItem(this.indexKey);
        if (indexData) {
          this.cacheIndex = new Set(JSON.parse(indexData));
        }
        if (this.cleanupOnInit) {
          await this.cleanupExpired();
        }
        this.initialized = true;
        const duration = Date.now() - startTime;
        console.log(`${this.logTag} Initialized (${this.cacheIndex.size} entries, ${duration}ms)`);
      } catch (error) {
        console.error(`${this.logTag} Error initializing cache:`, error);
        this.cacheIndex = new Set();
        this.initialized = true;
      } finally {
        this.initializationPromise = null;
      }
    })();

    return this.initializationPromise;
  }

  protected async persistIndex(): Promise<void> {
    try {
      await AsyncStorage.setItem(this.indexKey, JSON.stringify([...this.cacheIndex]));
    } catch (error) {
      console.error(`${this.logTag} Error persisting index:`, error);
    }
  }

  protected async evictOldestEntries(count: number): Promise<number> {
    const entries: Array<{ key: string; timestamp: number }> = [];
    for (const cacheKey of this.cacheIndex) {
      try {
        const cached = await AsyncStorage.getItem(cacheKey);
        if (cached) {
          const parsed: CachedEntry<TData> = JSON.parse(cached);
          entries.push({ key: cacheKey, timestamp: parsed.timestamp });
        }
      } catch {
        entries.push({ key: cacheKey, timestamp: 0 });
      }
    }
    entries.sort((a, b) => a.timestamp - b.timestamp);
    const keysToRemove = entries.slice(0, count).map((e) => e.key);
    if (keysToRemove.length === 0) return 0;
    await AsyncStorage.multiRemove(keysToRemove);
    keysToRemove.forEach((key) => this.cacheIndex.delete(key));
    await this.persistIndex();
    return keysToRemove.length;
  }

  protected async setItemWithQuotaHandling(cacheKey: string, value: string): Promise<boolean> {
    for (let attempt = 0; attempt < this.maxQuotaRetries; attempt++) {
      try {
        await AsyncStorage.setItem(cacheKey, value);
        this.quotaExhausted = false;
        return true;
      } catch (error) {
        if (!isQuotaExceededError(error)) throw error;
        const evicted = await this.evictOldestEntries(this.quotaEvictionBatch * (attempt + 1));
        if (evicted === 0) {
          this.quotaExhausted = true;
          return false;
        }
      }
    }
    this.quotaExhausted = true;
    return false;
  }

  protected async cleanupExpired(): Promise<void> {
    const keys = [...this.cacheIndex];
    let removedCount = 0;
    try {
      for (const cacheKey of keys) {
        const cached = await AsyncStorage.getItem(cacheKey);
        if (!cached) {
          this.cacheIndex.delete(cacheKey);
          removedCount++;
          continue;
        }
        try {
          const parsed: CachedEntry<TData> = JSON.parse(cached);
          const age = Date.now() - parsed.timestamp;
          if (age > parsed.ttl) {
            await AsyncStorage.removeItem(cacheKey);
            this.cacheIndex.delete(cacheKey);
            removedCount++;
          }
        } catch {
          await AsyncStorage.removeItem(cacheKey);
          this.cacheIndex.delete(cacheKey);
          removedCount++;
        }
      }
      if (removedCount > 0) {
        await this.persistIndex();
        console.log(`${this.logTag} Cleanup: removed ${removedCount} expired entries`);
      }
    } catch (error) {
      console.error(`${this.logTag} Error during cleanup:`, error);
    }
  }

  async getRaw(cacheKey: string): Promise<TData | null> {
    await this.initialize();
    try {
      const cached = await AsyncStorage.getItem(cacheKey);
      if (!cached) return null;
      const parsed: CachedEntry<TData> = JSON.parse(cached);
      const age = Date.now() - parsed.timestamp;
      if (age > parsed.ttl) {
        await this.removeRaw(cacheKey);
        return null;
      }
      return parsed.data;
    } catch (error) {
      console.error(`${this.logTag} Error reading cache for key ${cacheKey}:`, error);
      return null;
    }
  }

  async setRaw(cacheKey: string, data: TData, ttlMs?: number): Promise<boolean> {
    await this.initialize();
    if (this.quotaExhausted) return false;
    const entry: CachedEntry<TData> = {
      data,
      timestamp: Date.now(),
      ttl: ttlMs ?? this.defaultTtlMs,
    };
    try {
      const stored = await this.setItemWithQuotaHandling(cacheKey, JSON.stringify(entry));
      if (!stored) return false;
      this.cacheIndex.add(cacheKey);
      await this.persistIndex();
      return true;
    } catch (error) {
      if (isQuotaExceededError(error)) {
        this.quotaExhausted = true;
        return false;
      }
      console.error(`${this.logTag} Error storing key ${cacheKey}:`, error);
      return false;
    }
  }

  async removeRaw(cacheKey: string): Promise<void> {
    try {
      await AsyncStorage.removeItem(cacheKey);
      this.cacheIndex.delete(cacheKey);
      await this.persistIndex();
    } catch (error) {
      console.error(`${this.logTag} Error removing key ${cacheKey}:`, error);
    }
  }

  async clear(): Promise<void> {
    await this.initialize();
    try {
      const keys = [...this.cacheIndex];
      if (keys.length > 0) {
        await AsyncStorage.multiRemove(keys);
      }
      this.cacheIndex.clear();
      await AsyncStorage.removeItem(this.indexKey);
      console.log(`${this.logTag} Cleared ${keys.length} cache entries`);
    } catch (error) {
      console.error(`${this.logTag} Error clearing cache:`, error);
    }
  }

  async getCacheStats(): Promise<CacheStats> {
    await this.initialize();
    try {
      const keys = [...this.cacheIndex];
      const sizes = await Promise.all(
        keys.map(async (key) => {
          const data = await AsyncStorage.getItem(key);
          return data ? data.length : 0;
        })
      );
      const totalSize = sizes.reduce((sum, size) => sum + size, 0);
      return { totalEntries: keys.length, sizeKB: Math.round(totalSize / 1024) };
    } catch (error) {
      console.error(`${this.logTag} Error getting cache stats:`, error);
      return { totalEntries: 0, sizeKB: 0 };
    }
  }

  getIndexSize(): number {
    return this.cacheIndex.size;
  }

  isInitialized(): boolean {
    return this.initialized;
  }
}
